import { Dispatchable, IThunkDispatch, ThunkAction } from '../../store/middleware';
import { IState } from '../../store/reducer';
import { entriesOf } from './typing';
import { IMap } from './typing';

type DispatchableFn = (...a: any[]) => Dispatchable;
type Dispatcher<F extends DispatchableFn> = (...p: Parameters<F>) => any;

export type DispatchMap<A extends IMap<DispatchableFn> = {}> = { [OK in keyof A]: (...p: Parameters<A[OK]>) => any; };

const wrapDispatch = <A extends DispatchableFn>(fn: A, dispatch: IThunkDispatch): Dispatcher<A> =>
	(...p: Parameters<A>) => dispatch(fn(...p) as ThunkAction);

// Builds the typical mapDispatchToProps function from a map of action functions
export const buildMapDispatchToProps = <A extends IMap<DispatchableFn>, O extends DispatchMap<A>>
	(actions: A) => (dispatch: IThunkDispatch) => entriesOf(actions).reduce((curr, [k, f]) => ({ ...curr, [k]: wrapDispatch(f, dispatch) }), {} as O);

type StateMapFn = (state: IState, ...args: any[]) => IMap;
type DispatchMapFn = (dispatch: IThunkDispatch) => DispatchMap;
type OwnProps<S> = S extends (state: IState, ownProps: infer O) => IMap ? O : {};
type ContainerProps<S extends StateMapFn, D extends DispatchMapFn> = ReturnType<S> & ReturnType<D> & OwnProps<S>;

// Helper type which infers Container component props from the state-map and dispatch-map functions it will be connected to
export type Container<S extends StateMapFn, D extends DispatchMapFn> = (props: ContainerProps<S, D>) => any;

export const withSubsequentActions = <A extends (...p: any[]) => ThunkAction>(action: A, ...afters: Array<() => ThunkAction>) =>
	async (...params: Parameters<A>) => {
		await action(...params);
		Promise.all(afters.map((a) => a()));
	};
